/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from "react";
import {
  accessAPI,
  Titulo,
  Formulario,
  CampoInformacion,
  Boton,
  useIsMobile,
  TablaFiltrable
} from "../../../shared";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { Menu } from "primereact/menu";
import { Sidebar } from "../../../shared";
import { Toast } from "primereact/toast";
import { Stepper } from "primereact/stepper";
import { StepperPanel } from "primereact/stepperpanel";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";

// Los campos adicionales se mezclan en el mismo objeto plano que los nativos
// para poder reutilizar el componente Formulario. Para distinguirlos al hacer
// submit usamos este prefijo en el `nombre` del campo.
const PREFIJO_ADICIONAL = "adicional_";

// Separa los campos planos del Formulario en nativos del Sitio y la lista
// [{nombreCampo, valor}] de adicionales. Filtra los adicionales vacíos.
function separarCamposAdicionales(campos) {
  const nativos = {};
  const camposAdicionales = [];
  for (const [key, valor] of Object.entries(campos)) {
    if (key.startsWith(PREFIJO_ADICIONAL)) {
      const nombreCampo = key.slice(PREFIJO_ADICIONAL.length);
      const texto = valor == null ? "" : String(valor).trim();
      if (texto) camposAdicionales.push({ nombreCampo, valor: texto });
    } else {
      nativos[key] = valor;
    }
  }
  return { nativos, camposAdicionales };
}

export default function CardSitios({ clienteId, sitios, onSitiosChanged }) {
  const [tiposSitio, setTiposSitio] = useState([]);
  const [sidebarNuevoVisible, setSidebarNuevoVisible] = useState(false);
  const [sidebarDetallesVisible, setSidebarDetallesVisible] = useState(false);
  const [sidebarEditarVisible, setSidebarEditarVisible] = useState(false);
  const [selectedSitio, setSelectedSitio] = useState(null);
  const [loaderNuevo, setLoaderNuevo] = useState(false);
  const [loaderEditar, setLoaderEditar] = useState(false);

  // Estado del Stepper de crear sitio
  const [pasoNuevo, setPasoNuevo] = useState(0);
  const [sitioTipoIdNuevo, setSitioTipoIdNuevo] = useState(null);
  const [formPaso1KeyNuevo, setFormPaso1KeyNuevo] = useState(0);
  const [formPaso2KeyNuevo, setFormPaso2KeyNuevo] = useState(0);

  // Estado del Stepper de editar sitio
  const [pasoEditar, setPasoEditar] = useState(0);
  const [sitioTipoIdEditar, setSitioTipoIdEditar] = useState(null);
  const [formPaso1KeyEditar, setFormPaso1KeyEditar] = useState(0);
  const [formPaso2KeyEditar, setFormPaso2KeyEditar] = useState(0);

  const toast = useRef(null);
  const menuRef = useRef(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    cargarTiposSitio();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function cargarTiposSitio() {
    accessAPI(
      "GET",
      "admin/sitio/tipos",
      null,
      (respuesta) => setTiposSitio(respuesta),
      () => {}
    );
  }

  function crearSitio(campos) {
    const { nativos, camposAdicionales } = separarCamposAdicionales(campos);
    setLoaderNuevo(true);
    accessAPI(
      "POST",
      "admin/sitio",
      {
        ...nativos,
        clienteId,
        sitioTipoId: sitioTipoIdNuevo,
        camposAdicionales
      },
      () => {
        setLoaderNuevo(false);
        setSidebarNuevoVisible(false);
        if (onSitiosChanged) onSitiosChanged();
        toast.current?.show({
          severity: "success",
          summary: "Sitio creado",
          life: 3000
        });
      },
      (respuesta) => {
        setLoaderNuevo(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al crear sitio",
          sticky: true
        });
      }
    );
  }

  function editarSitio(campos) {
    const { nativos, camposAdicionales } = separarCamposAdicionales(campos);
    setLoaderEditar(true);
    accessAPI(
      "PUT",
      `admin/sitio/${selectedSitio.id}`,
      {
        ...nativos,
        sitioTipoId: sitioTipoIdEditar,
        camposAdicionales
      },
      () => {
        setLoaderEditar(false);
        setSidebarEditarVisible(false);
        if (onSitiosChanged) onSitiosChanged();
        toast.current?.show({
          severity: "success",
          summary: "Sitio actualizado",
          life: 3000
        });
      },
      (respuesta) => {
        setLoaderEditar(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al actualizar sitio",
          sticky: true
        });
      }
    );
  }

  function eliminarSitio(sitio) {
    confirmDialog({
      message: `¿Eliminar el sitio "${sitio.nombre || sitio.direccion}"?`,
      header: "Confirmar eliminación",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Eliminar",
      rejectLabel: "Cancelar",
      acceptClassName: "p-button-danger",
      accept: () => {
        accessAPI(
          "DELETE",
          `admin/sitio/${sitio.id}`,
          null,
          () => {
            if (onSitiosChanged) onSitiosChanged();
            toast.current?.show({
              severity: "success",
              summary: "Sitio eliminado",
              life: 3000
            });
          },
          (respuesta) => {
            toast.current?.show({
              severity: "error",
              summary: "Error",
              detail: respuesta[0]?.msg || "Error al eliminar sitio",
              sticky: true
            });
          }
        );
      }
    });
  }

  function cambiarHeadquarters(sitio) {
    confirmDialog({
      message: `¿Marcar "${sitio.nombre || sitio.direccion}" como sede principal?`,
      header: "Cambiar sede principal",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Confirmar",
      rejectLabel: "Cancelar",
      accept: () => {
        accessAPI(
          "PUT",
          `admin/sitio/headquarters/${sitio.id}`,
          null,
          () => {
            if (onSitiosChanged) onSitiosChanged();
            toast.current?.show({
              severity: "success",
              summary: "Sede principal actualizada",
              life: 3000
            });
          },
          (respuesta) => {
            toast.current?.show({
              severity: "error",
              summary: "Error",
              detail: respuesta[0]?.msg || "Error al cambiar sede principal",
              sticky: true
            });
          }
        );
      }
    });
  }

  function abrirSidebarNuevo() {
    setSitioTipoIdNuevo(null);
    setPasoNuevo(0);
    setFormPaso1KeyNuevo((k) => k + 1);
    setFormPaso2KeyNuevo((k) => k + 1);
    setSidebarNuevoVisible(true);
  }

  function abrirSidebarEditar(sitio) {
    setSelectedSitio(sitio);
    setSitioTipoIdEditar(sitio.sitioTipoId);
    setPasoEditar(0);
    setFormPaso1KeyEditar((k) => k + 1);
    setFormPaso2KeyEditar((k) => k + 1);
    setSidebarEditarVisible(true);
  }

  // Opciones del menú contextual para cada sitio
  const menuItems = selectedSitio
    ? [
        ...(!selectedSitio.headquarters
          ? [
              {
                label: "Marcar como sede principal",
                icon: "pi pi-home",
                command: () => cambiarHeadquarters(selectedSitio)
              }
            ]
          : []),
        {
          label: "Editar",
          icon: "pi pi-pencil",
          command: () => abrirSidebarEditar(selectedSitio)
        },
        ...(!selectedSitio.headquarters
          ? [
              {
                label: "Eliminar",
                icon: "pi pi-trash",
                command: () => eliminarSitio(selectedSitio)
              }
            ]
          : [])
      ]
    : [];

  const opcionesTipo = tiposSitio
    .map((t) => ({
      value: t.id,
      etiqueta: t.nombre
    }))
    .sort((a, b) => a.etiqueta.localeCompare(b.etiqueta, "es"));

  // Devuelve la lista de nombres de campos adicionales definidos por el tipo
  // de sitio indicado. Devuelve [] si el tipo no se encontró o no tiene campos.
  function camposAdicionalesDeTipo(tipoId) {
    if (!tipoId) return [];
    const tipo = tiposSitio.find((t) => t.id === tipoId);
    if (!tipo) return [];
    return Array.isArray(tipo.camposAdicionales) ? tipo.camposAdicionales : [];
  }

  // Arma los items del Formulario que corresponden a los campos adicionales
  // del tipo. `valoresPrevios` es un map { nombreCampo: valor } para precargar.
  function camposAdicionalesParaForm(tipoId, valoresPrevios = {}) {
    return camposAdicionalesDeTipo(tipoId).map((nombre) => ({
      nombre: `${PREFIJO_ADICIONAL}${nombre}`,
      tipoitemformularioid: 1,
      etiqueta: nombre,
      respuesta: valoresPrevios[nombre] ?? ""
    }));
  }

  // Form config del paso 1 al crear sitio: un solo dropdown con los tipos.
  // Al validar y avanzar, guardamos el tipo elegido y vamos al paso 2.
  const formPaso1NuevoConfig = {
    secciones: [
      {
        campos: [
          {
            nombre: "sitioTipoId",
            tipoitemformularioid: 5,
            etiqueta: "Tipo de sitio",
            requerido: true,
            sinOpcionInicial: true,
            opciones: opcionesTipo,
            respuesta: sitioTipoIdNuevo
          }
        ]
      }
    ],
    botones: [
      {
        etiqueta: "Siguiente",
        validar: true,
        callback: (campos) => {
          setSitioTipoIdNuevo(campos.sitioTipoId);
          setFormPaso2KeyNuevo((k) => k + 1);
          setPasoNuevo(1);
        }
      }
    ]
  };

  // Form config del paso 1 al editar sitio: igual al de crear pero precargado
  // con el tipo actual del sitio.
  const formPaso1EditarConfig = selectedSitio
    ? {
        secciones: [
          {
            campos: [
              {
                nombre: "sitioTipoId",
                tipoitemformularioid: 5,
                etiqueta: "Tipo de sitio",
                requerido: true,
                sinOpcionInicial: true,
                opciones: opcionesTipo,
                respuesta: sitioTipoIdEditar
              }
            ]
          }
        ],
        botones: [
          {
            etiqueta: "Siguiente",
            validar: true,
            callback: (campos) => {
              setSitioTipoIdEditar(campos.sitioTipoId);
              setFormPaso2KeyEditar((k) => k + 1);
              setPasoEditar(1);
            }
          }
        ]
      }
    : null;

  // Form config del paso 2 al crear sitio (campos nativos + adicionales del
  // tipo seleccionado en paso 1).
  const formNuevoConfig = {
    secciones: [
      {
        campos: [
          {
            nombre: "nombre",
            tipoitemformularioid: 1,
            etiqueta: "Nombre"
          },
          {
            nombre: "direccion",
            tipoitemformularioid: 1,
            etiqueta: "Dirección",
            requerido: true
          },
          {
            nombre: "direccion2",
            tipoitemformularioid: 1,
            etiqueta: "Dirección 2"
          },
          {
            nombre: "codigoPostal",
            tipoitemformularioid: 1,
            etiqueta: "Código postal"
          },
          {
            nombre: "ciudad",
            tipoitemformularioid: 1,
            etiqueta: "Ciudad"
          },
          {
            nombre: "estado",
            tipoitemformularioid: 1,
            etiqueta: "Estado"
          },
          {
            nombre: "pais",
            tipoitemformularioid: 1,
            etiqueta: "País"
          },
          {
            nombre: "telefono",
            tipoitemformularioid: 1,
            etiqueta: "Teléfono"
          },
          ...camposAdicionalesParaForm(sitioTipoIdNuevo)
        ]
      }
    ],
    botones: [
      {
        etiqueta: "Atrás",
        transparente: true,
        callback: () => setPasoNuevo(0)
      },
      {
        etiqueta: "Crear sitio",
        validar: true,
        callback: crearSitio
      }
    ]
  };

  // Form config del paso 2 al editar sitio. Si el tipo cambió respecto al
  // original del sitio, no precargamos valores adicionales viejos (no tienen
  // sentido para los nuevos campos).
  const formEditarConfig = selectedSitio
    ? (() => {
        const tipoOriginal = selectedSitio.sitioTipoId;
        const adicionalesPrevios =
          sitioTipoIdEditar === tipoOriginal &&
          Array.isArray(selectedSitio.camposAdicionales)
            ? selectedSitio.camposAdicionales
            : [];
        const mapPrevios = Object.fromEntries(
          adicionalesPrevios.map((v) => [v.nombreCampo, v.valor])
        );
        return {
          secciones: [
            {
              campos: [
                {
                  nombre: "nombre",
                  tipoitemformularioid: 1,
                  etiqueta: "Nombre",
                  requerido: true,
                  respuesta: selectedSitio.nombre
                },
                {
                  nombre: "direccion",
                  tipoitemformularioid: 1,
                  etiqueta: "Dirección",
                  requerido: true,
                  respuesta: selectedSitio.direccion
                },
                {
                  nombre: "direccion2",
                  tipoitemformularioid: 1,
                  etiqueta: "Dirección 2",
                  respuesta: selectedSitio.direccion2
                },
                {
                  nombre: "codigoPostal",
                  tipoitemformularioid: 1,
                  etiqueta: "Código postal",
                  requerido: true,
                  respuesta: selectedSitio.codigoPostal
                },
                {
                  nombre: "ciudad",
                  tipoitemformularioid: 1,
                  etiqueta: "Ciudad",
                  requerido: true,
                  respuesta: selectedSitio.ciudad
                },
                {
                  nombre: "estado",
                  tipoitemformularioid: 1,
                  etiqueta: "Estado",
                  requerido: true,
                  respuesta: selectedSitio.estado
                },
                {
                  nombre: "pais",
                  tipoitemformularioid: 1,
                  etiqueta: "País",
                  requerido: true,
                  respuesta: selectedSitio.pais
                },
                {
                  nombre: "telefono",
                  tipoitemformularioid: 1,
                  etiqueta: "Teléfono",
                  respuesta: selectedSitio.telefono
                },
                ...camposAdicionalesParaForm(sitioTipoIdEditar, mapPrevios)
              ]
            }
          ],
          botones: [
            {
              etiqueta: "Atrás",
              transparente: true,
              callback: () => setPasoEditar(0)
            },
            {
              etiqueta: "Guardar cambios",
              validar: true,
              callback: editarSitio
            }
          ]
        };
      })()
    : null;

  // Un sitio queda "libre" si no es referenciado por ningún proceso ni
  // solicitud (ni como fábrica ni como fabricante). El backend manda los
  // 4 counts en _count; los sumamos para decidir.
  function estaVinculado(s) {
    const c = s._count || {};
    return (
      (c.procesosFabrica || 0) +
        (c.procesosFabricante || 0) +
        (c.solicitudProcesosFabrica || 0) +
        (c.solicitudProcesosFabricante || 0) >
      0
    );
  }
  const sitiosLibres = sitios.filter((s) => !estaVinculado(s));
  const sitiosVinculados = sitios.filter(estaVinculado);

  // Columnas compartidas por las dos tablas. DataTable itera sus children
  // buscando <Column> directos; si los envolvemos en Fragment no las
  // encuentra. Por eso devolvemos un array con keys.
  const columnasTabla = [
    <Column
      key="tipo"
      header="Tipo"
      body={(s) => (
        <div css={{ display: "flex", alignItems: "center", gap: "0.5em" }}>
          {s.sitioTipo?.nombre}
          {s.headquarters && (
            <Tag
              value="Sede principal"
              severity="info"
              css={{ fontSize: "0.7em" }}
            />
          )}
        </div>
      )}
    />,
    !isMobile && (
      <Column key="nombre" header="Nombre" body={(s) => s.nombre || "-"} />
    ),
    <Column
      key="direccion"
      header="Dirección"
      body={(s) => (
        <div
          css={{
            display: "flex",
            alignItems: "center",
            gap: "0.5em",
            flexWrap: "wrap"
          }}
        >
          <span>{s.direccion || "-"}</span>
          {!s.direccionFormateada && (
            <Tag
              icon="pi pi-exclamation-triangle"
              value="Sin formato"
              severity="warning"
              css={{ fontSize: "0.7em" }}
            />
          )}
        </div>
      )}
    />,
    <Column
      key="acciones"
      body={(row) => (
        <Boton
          texto
          negro
          callback={(e) => {
            setSelectedSitio(row);
            menuRef.current.toggle(e);
          }}
        >
          <i className="pi pi-ellipsis-v" />
        </Boton>
      )}
      style={{ width: "60px", textAlign: "center" }}
    />
  ];

  // Valores adicionales del sitio actual para mostrar en el panel de detalles.
  const adicionalesSitio =
    selectedSitio && Array.isArray(selectedSitio.camposAdicionales)
      ? selectedSitio.camposAdicionales
      : [];

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <ConfirmDialog />
      <Menu model={menuItems} popup ref={menuRef} />

      <Card>
        <Titulo
          botones={
            <Boton icono negro callback={abrirSidebarNuevo}>
              <i className="pi pi-plus" />
            </Boton>
          }
        >
          Sitios del cliente
        </Titulo>
        <TablaFiltrable conFiltros={false}
          value={sitiosLibres}
          dataKey="id"
          stripedRows
          emptyMessage="No hay sitios sin vincular a procesos"
          onRowClick={(e) => {
            const target = e.originalEvent.target;
            if (target.closest("button") || target.closest(".p-menu")) return;
            setSelectedSitio(e.data);
            setSidebarDetallesVisible(true);
          }}
          rowClassName={() => "cursor-pointer"}
          nombreArchivo="sitios_libres_cliente"
        >
          {columnasTabla}
        </TablaFiltrable>
      </Card>

      {sitiosVinculados.length > 0 && (
        <Card css={{ marginTop: "1em" }}>
          <Titulo>Sitios vinculados a procesos</Titulo>
          <TablaFiltrable conFiltros={false}
            value={sitiosVinculados}
            dataKey="id"
            stripedRows
            onRowClick={(e) => {
              const target = e.originalEvent.target;
              if (target.closest("button") || target.closest(".p-menu")) return;
              setSelectedSitio(e.data);
              setSidebarDetallesVisible(true);
            }}
            rowClassName={() => "cursor-pointer"}
            nombreArchivo="sitios_vinculados_cliente"
          >
            {columnasTabla}
          </TablaFiltrable>
        </Card>
      )}

      {/* Sidebar detalles del sitio */}
      <Sidebar
        visible={sidebarDetallesVisible}
        onHide={() => setSidebarDetallesVisible(false)}
        position="right"
        className="w-full lg:w-30rem md:w-30rem"
      >
        {selectedSitio && (
          <div>
            <Titulo>{selectedSitio.nombre || selectedSitio.sitioTipo?.nombre}</Titulo>
            {!selectedSitio.direccionFormateada && (
              <div css={{ marginBottom: "1em" }}>
                <Tag
                  icon="pi pi-exclamation-triangle"
                  value="Dirección sin formato — revisar los campos"
                  severity="warning"
                />
              </div>
            )}
            <div className="grid">
              <CampoInformacion nombreCampo="Tipo">
                {selectedSitio.sitioTipo?.nombre}
                {selectedSitio.headquarters && " (Sede principal)"}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Dirección">
                {selectedSitio.direccion || "-"}
              </CampoInformacion>
              {selectedSitio.direccion2 && (
                <CampoInformacion nombreCampo="Dirección 2">
                  {selectedSitio.direccion2}
                </CampoInformacion>
              )}
              <CampoInformacion nombreCampo="Código postal">
                {selectedSitio.codigoPostal || "-"}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Ciudad">
                {selectedSitio.ciudad || "-"}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Estado">
                {selectedSitio.estado || "-"}
              </CampoInformacion>
              <CampoInformacion nombreCampo="País">
                {selectedSitio.pais || "-"}
              </CampoInformacion>
              {selectedSitio.telefono && (
                <CampoInformacion nombreCampo="Teléfono">
                  {selectedSitio.telefono}
                </CampoInformacion>
              )}
              {adicionalesSitio.map((c) => (
                <CampoInformacion key={c.nombreCampo} nombreCampo={c.nombreCampo}>
                  {c.valor || "-"}
                </CampoInformacion>
              ))}
            </div>
          </div>
        )}
      </Sidebar>

      {/* Sidebar nuevo sitio */}
      <Sidebar
        visible={sidebarNuevoVisible}
        onHide={() => setSidebarNuevoVisible(false)}
        position="right"
        className="w-full lg:w-30rem md:w-30rem"
      >
        <Stepper
          activeStep={pasoNuevo}
          onChangeStep={(e) => {
            if (e.index === 1 && !sitioTipoIdNuevo) return;
            if (e.index === 1) setFormPaso2KeyNuevo((k) => k + 1);
            setPasoNuevo(e.index);
          }}
          headerPosition="top"
        >
          <StepperPanel header="Tipo">
            <Formulario
              key={formPaso1KeyNuevo}
              config={formPaso1NuevoConfig}
            />
          </StepperPanel>

          <StepperPanel header="Datos">
            <Formulario
              key={formPaso2KeyNuevo}
              config={formNuevoConfig}
              loader={loaderNuevo}
            />
          </StepperPanel>
        </Stepper>
      </Sidebar>

      {/* Sidebar editar sitio */}
      <Sidebar
        visible={sidebarEditarVisible}
        onHide={() => setSidebarEditarVisible(false)}
        position="right"
        className="w-full lg:w-30rem md:w-30rem"
      >
        {selectedSitio && (
          <Stepper
            activeStep={pasoEditar}
            onChangeStep={(e) => {
              if (e.index === 1 && !sitioTipoIdEditar) return;
              if (e.index === 1) setFormPaso2KeyEditar((k) => k + 1);
              setPasoEditar(e.index);
            }}
            headerPosition="top"
          >
            <StepperPanel header="Tipo">
              {formPaso1EditarConfig && (
                <Formulario
                  key={formPaso1KeyEditar}
                  config={formPaso1EditarConfig}
                />
              )}
            </StepperPanel>

            <StepperPanel header="Datos">
              {sitioTipoIdEditar !== selectedSitio.sitioTipoId && (
                <div
                  css={{
                    marginBottom: "1em",
                    color: "var(--grisTextos)",
                    fontSize: "0.85em"
                  }}
                >
                  Cambiaste el tipo de sitio — los valores de los campos
                  adicionales del tipo anterior fueron descartados.
                </div>
              )}
              {formEditarConfig && (
                <Formulario
                  key={formPaso2KeyEditar}
                  config={formEditarConfig}
                  loader={loaderEditar}
                />
              )}
            </StepperPanel>
          </Stepper>
        )}
      </Sidebar>
    </>
  );
}
